import {
  CATEGORIES,
  COMMERCIAL_OPTIONS,
  HAKLAI_ADD_MODES,
  HAKLAI_PTOR_TYPES,
  LAND_AND_SHARED_OPTIONS,
  MAX_MONEY_DIGITS,
  MAX_RESIDENTIAL,
  RESIDENTIAL_LABELS
} from "../../shared/haklaiConstants";
import {
  buildSortedPropertyPartRows,
  getHaklaiZabanFieldError,
  hasPositiveNumericValue,
  isHaklaiBuiltValueRelevant,
  isIgnorableTrailingPropertyPartRow,
  isVisuallyDraftTrailingPropertyPartRow
} from "../../shared/haklaiFlowHelpers";
import { parseHaklaiClassicMoneyInput } from "../../shared/haklaiMoneyHelpers";
import { normalizeHaklaiExpensesForDependencies } from "../../expenses/logic/haklaiExpensesHelpers";
import { HAKLAI_PROPERTY_PART_ERRORS } from "../haklaiPropertyPartsTexts";

// ─── Key predicates ──────────────────────────────────────────────────────────

export const isCommercialPartKey = (key) => /^commercial_\d+$/.test(key || "");
export const isCommercialBgPartKey = (key) => /^commercialBg_\d+$/.test(key || "");

// ─── Part factory & normalization ────────────────────────────────────────────

// `assetTypeKey` is the canonical identity of an editable-name part (e.g.
// "warehouse"). It is what we persist for matching against the static option
// lists. `label` is the snapshot of the Hebrew display text at write time —
// kept alongside so existing label-driven code paths and reports keep working
// without an extra lookup.
export const createHaklaiPropertyPart = ({
  category,
  label = "",
  assetTypeKey = "",
  overrides = {}
} = {}) => ({
  label,
  assetTypeKey,
  category,
  value: null,
  valueWithoutZaban: null,
  ptorType: HAKLAI_PTOR_TYPES.NONE,
  constructionsDoneBeforeApril1997: false,
  ...overrides
});

// Resolves `assetTypeKey` from legacy payloads that only stored the Hebrew
// label, by matching against the static option lists for the category.
const resolveLegacyAssetTypeKey = (category, label) => {
  const trimmedLabel = String(label || "").trim();
  if (!trimmedLabel) return "";
  if (category === CATEGORIES.COMMERCIAL || category === CATEGORIES.COMMERCIAL_BG) {
    return COMMERCIAL_OPTIONS.find((opt) => opt.label === trimmedLabel)?.key || "";
  }
  if (category === CATEGORIES.LAND_AND_SHARED) {
    return LAND_AND_SHARED_OPTIONS.find((opt) => opt.label === trimmedLabel)?.key || "";
  }
  return "";
};

const normalizeHaklaiPropertyPart = (part = {}) => {
  const normalizedPart = { ...(part || {}) };
  delete normalizedPart.acquisitionCost;
  delete normalizedPart.isRented;

  const category = normalizedPart?.category;
  const label = normalizedPart?.label || "";
  const persistedAssetTypeKey = String(normalizedPart?.assetTypeKey || "").trim();
  const assetTypeKey = persistedAssetTypeKey || resolveLegacyAssetTypeKey(category, label);

  return createHaklaiPropertyPart({
    category,
    label,
    assetTypeKey,
    overrides: {
      ...normalizedPart,
      assetTypeKey,
      constructionsDoneBeforeApril1997: normalizedPart?.constructionsDoneBeforeApril1997 === true
    }
  });
};

export const normalizeHaklaiPropertyPartsData = (propertyPartsData = {}) => (
  Object.entries(propertyPartsData || {}).reduce((acc, [key, part]) => {
    acc[key] = normalizeHaklaiPropertyPart(part);
    return acc;
  }, {})
);

// ─── Key generation ──────────────────────────────────────────────────────────

export const findNextResidentialSlot = (propertyPartsData = {}) => {
  for (let index = 1; index <= MAX_RESIDENTIAL; index++) {
    const key = `residence_${index}`;
    if (propertyPartsData[key]) continue;

    const previousKey = index > 1 ? `residence_${index - 1}` : "";
    const previousExists = previousKey ? Boolean(propertyPartsData[previousKey]) : true;
    if (!previousExists) break;

    return { key, label: RESIDENTIAL_LABELS[index - 1] };
  }
  return null;
};

export const getNextCategoryKey = ({ categoryId, propertyPartsData = {} }) => {
  if (categoryId === CATEGORIES.RESIDENTIAL) {
    return findNextResidentialSlot(propertyPartsData)?.key || "";
  }
  return "";
};

// ─── Compaction & removal ────────────────────────────────────────────────────

const compactIndexedCategoryParts = (
  propertyPartsData = {},
  removedKey,
  category,
  keyPrefix,
  labelsByIndex = null
) => {
  const nextParts = { ...propertyPartsData };
  const remainingEntries = Object.entries(propertyPartsData)
    .filter(([key, part]) => key !== removedKey && part?.category === category)
    .sort(([a], [b]) => Number(a.split("_")[1]) - Number(b.split("_")[1]));

  Object.keys(nextParts).forEach((key) => {
    if (nextParts[key]?.category === category) delete nextParts[key];
  });

  const remappedKeys = {};
  remainingEntries.forEach(([oldKey, part], index) => {
    const newKey = `${keyPrefix}_${index + 1}`;
    nextParts[newKey] = {
      ...part,
      ...(Array.isArray(labelsByIndex) ? { label: labelsByIndex[index] || part.label || "" } : {})
    };
    remappedKeys[oldKey] = newKey;
  });

  return { nextParts, remappedKeys };
};

const remapExpenseDepreciationByPart = (
  expense = {},
  remappedKeys = {},
  removedKeys = []
) => {
  const depreciationByPart = (
    expense.depreciationByPart
    && typeof expense.depreciationByPart === "object"
    && !Array.isArray(expense.depreciationByPart)
  ) ? expense.depreciationByPart : {};
  const removedKeySet = new Set((Array.isArray(removedKeys) ? removedKeys : []).filter(Boolean));
  const nextDepreciationByPart = Object.entries(depreciationByPart).reduce((acc, [oldKey, value]) => {
    if (removedKeySet.has(oldKey)) return acc;
    acc[remappedKeys[oldKey] || oldKey] = value;
    return acc;
  }, {});
  return {
    ...expense,
    depreciationByPart: nextDepreciationByPart
  };
};

const remapPartKeyedObject = (
  source = {},
  remappedKeys = {},
  removedKeys = []
) => {
  const objectByPart = (
    source
    && typeof source === "object"
    && !Array.isArray(source)
  ) ? source : {};
  const removedKeySet = new Set((Array.isArray(removedKeys) ? removedKeys : []).filter(Boolean));
  return Object.entries(objectByPart).reduce((acc, [oldKey, value]) => {
    if (removedKeySet.has(oldKey)) return acc;
    acc[remappedKeys[oldKey] || oldKey] = value;
    return acc;
  }, {});
};

export const removeHaklaiPropertyPartKeys = ({
  propertyPartsData = {},
  expenses = [],
  residentialRentalByPart = {},
  purchaseDepreciationByPart = {},
  purchaseDepreciationSelectionByPart = {},
  purchaseZabanAttributionByPart = {},
  keys = [],
  hasZaban = false,
  totalPropertyCost = null
}) => {
  const uniqueKeys = [...new Set((Array.isArray(keys) ? keys : []).filter(Boolean))];
  let nextParts = propertyPartsData || {};
  let nextExpenses = Array.isArray(expenses) ? expenses : [];
  let nextResidentialRentalByPart = { ...(residentialRentalByPart || {}) };
  let nextPurchaseDepreciationByPart = { ...(purchaseDepreciationByPart || {}) };
  let nextPurchaseDepreciationSelectionByPart = { ...(purchaseDepreciationSelectionByPart || {}) };
  let changed = false;

  uniqueKeys.forEach((key) => {
    const currentPart = nextParts[key];
    if (!currentPart) return;

    if (/^residence_\d+$/.test(key) && currentPart.category === CATEGORIES.RESIDENTIAL) {
      const residentialCount = Object.values(nextParts)
        .filter((part) => part?.category === CATEGORIES.RESIDENTIAL)
        .length;
      if (residentialCount <= 1) return;

      changed = true;
      const { nextParts: compactedParts, remappedKeys } = compactIndexedCategoryParts(
        nextParts,
        key,
        CATEGORIES.RESIDENTIAL,
        "residence",
        RESIDENTIAL_LABELS
      );
      nextParts = compactedParts;
      nextExpenses = nextExpenses
        .filter((expense) => expense.targetPartKey !== key)
        .map((expense) => (
          remapExpenseDepreciationByPart(
            remappedKeys[expense.targetPartKey]
              ? { ...expense, targetPartKey: remappedKeys[expense.targetPartKey] }
              : expense,
            remappedKeys,
            [key]
          )
        ));
      nextPurchaseDepreciationByPart = remapPartKeyedObject(nextPurchaseDepreciationByPart, remappedKeys, [key]);
      nextResidentialRentalByPart = remapPartKeyedObject(nextResidentialRentalByPart, remappedKeys, [key]);
      nextPurchaseDepreciationSelectionByPart = remapPartKeyedObject(
        nextPurchaseDepreciationSelectionByPart,
        remappedKeys,
        [key]
      );
      return;
    }

    changed = true;

    if (isCommercialPartKey(key) && currentPart.category === CATEGORIES.COMMERCIAL) {
      const { nextParts: compactedParts, remappedKeys } = compactIndexedCategoryParts(
        nextParts, key, CATEGORIES.COMMERCIAL, "commercial"
      );
      nextParts = compactedParts;
      nextExpenses = nextExpenses
        .filter((expense) => expense.targetPartKey !== key)
        .map((expense) => (
          remapExpenseDepreciationByPart(
            remappedKeys[expense.targetPartKey]
              ? { ...expense, targetPartKey: remappedKeys[expense.targetPartKey] }
              : expense,
            remappedKeys,
            [key]
          )
        ));
      nextPurchaseDepreciationByPart = Object.entries(nextPurchaseDepreciationByPart).reduce((acc, [oldKey, value]) => {
        if (oldKey === key) return acc;
        acc[remappedKeys[oldKey] || oldKey] = value;
        return acc;
      }, {});
      nextResidentialRentalByPart = remapPartKeyedObject(nextResidentialRentalByPart, remappedKeys, [key]);
      nextPurchaseDepreciationSelectionByPart = remapPartKeyedObject(
        nextPurchaseDepreciationSelectionByPart,
        remappedKeys,
        [key]
      );
      return;
    }

    if (isCommercialBgPartKey(key) && currentPart.category === CATEGORIES.COMMERCIAL_BG) {
      const { nextParts: compactedParts, remappedKeys } = compactIndexedCategoryParts(
        nextParts, key, CATEGORIES.COMMERCIAL_BG, "commercialBg"
      );
      nextParts = compactedParts;
      nextExpenses = nextExpenses
        .filter((expense) => expense.targetPartKey !== key)
        .map((expense) => (
          remapExpenseDepreciationByPart(
            remappedKeys[expense.targetPartKey]
              ? { ...expense, targetPartKey: remappedKeys[expense.targetPartKey] }
              : expense,
            remappedKeys,
            [key]
          )
        ));
      nextPurchaseDepreciationByPart = Object.entries(nextPurchaseDepreciationByPart).reduce((acc, [oldKey, value]) => {
        if (oldKey === key) return acc;
        acc[remappedKeys[oldKey] || oldKey] = value;
        return acc;
      }, {});
      nextResidentialRentalByPart = remapPartKeyedObject(nextResidentialRentalByPart, remappedKeys, [key]);
      nextPurchaseDepreciationSelectionByPart = remapPartKeyedObject(
        nextPurchaseDepreciationSelectionByPart,
        remappedKeys,
        [key]
      );
      return;
    }

    nextParts = { ...nextParts };
    delete nextParts[key];
    nextExpenses = nextExpenses
      .filter((expense) => expense.targetPartKey !== key)
      .map((expense) => remapExpenseDepreciationByPart(expense, {}, [key]));
    if (Object.prototype.hasOwnProperty.call(nextPurchaseDepreciationByPart, key)) {
      nextPurchaseDepreciationByPart = { ...nextPurchaseDepreciationByPart };
      delete nextPurchaseDepreciationByPart[key];
    }
    if (Object.prototype.hasOwnProperty.call(nextResidentialRentalByPart, key)) {
      nextResidentialRentalByPart = { ...nextResidentialRentalByPart };
      delete nextResidentialRentalByPart[key];
    }
    if (Object.prototype.hasOwnProperty.call(nextPurchaseDepreciationSelectionByPart, key)) {
      nextPurchaseDepreciationSelectionByPart = { ...nextPurchaseDepreciationSelectionByPart };
      delete nextPurchaseDepreciationSelectionByPart[key];
    }
  });

  if (!changed) {
    return {
      changed: false,
      propertyPartsData,
      expenses,
      residentialRentalByPart,
      purchaseDepreciationByPart,
      purchaseDepreciationSelectionByPart,
      purchaseZabanAttributionByPart
    };
  }

  const normalizedExpensesState = normalizeHaklaiExpensesForDependencies(
    nextExpenses,
    nextParts,
    hasZaban,
    totalPropertyCost
  );

  return {
    changed: true,
    propertyPartsData: nextParts,
    expenses: normalizedExpensesState.expenses,
    residentialRentalByPart: nextResidentialRentalByPart,
    purchaseDepreciationByPart: nextPurchaseDepreciationByPart,
    purchaseDepreciationSelectionByPart: nextPurchaseDepreciationSelectionByPart,
    purchaseZabanAttributionByPart: {}
  };
};

// ─── Row-level validation ────────────────────────────────────────────────────

// Duplicate type selections within a category are no longer treated as an
// error: they are disambiguated automatically at display time by
// `getHaklaiPartDisplayLabel` (auto-numbered (1), (2), ...).
export const getPropertyPartRowValidationState = ({
  rowKey,
  part,
  categoryRows = [],
  hasZaban = false
}) => {
  const rowIndex = categoryRows.findIndex(([key]) => key === rowKey);
  const isTrailingIgnorable = rowIndex !== -1 && isIgnorableTrailingPropertyPartRow(categoryRows, rowIndex);
  const isVisuallyDrafting = rowIndex !== -1 && isVisuallyDraftTrailingPropertyPartRow(categoryRows, rowIndex);
  const hasPositiveSaleValue = hasPositiveNumericValue(part?.value);
  const zabanError = getHaklaiZabanFieldError(part, hasZaban);
  const immediateZabanError = Boolean(
    zabanError && zabanError !== HAKLAI_PROPERTY_PART_ERRORS.zabanRequired
  );
  const readinessMissingFields = [];

  if (!hasPositiveSaleValue) readinessMissingFields.push("value");
  if (zabanError === HAKLAI_PROPERTY_PART_ERRORS.zabanRequired) {
    readinessMissingFields.push("zabanAmount");
  }

  const invalidFields = [
    ...(immediateZabanError ? ["zabanAmount"] : []),
    ...readinessMissingFields
  ].filter((fieldName, index, src) => src.indexOf(fieldName) === index);

  return {
    isTrailingIgnorable,
    isVisuallyDrafting,
    requiresBuiltValue: isHaklaiBuiltValueRelevant(part, hasZaban),
    immediateZabanError,
    zabanError,
    readinessMissingFields,
    isReady: invalidFields.length === 0,
    firstInvalidField: invalidFields[0] || "",
    invalidFields
  };
};

// ─── Validation overlay (per-cell error messages) ───────────────────────────

// Message shown inline on the `value` cell when cumulative residential
// allocation pushes the running sale balance below zero.
export const PROPERTY_PART_VALUE_OVERSHOOT_MESSAGE = HAKLAI_PROPERTY_PART_ERRORS.valueOvershoot;

const getPropertyPartFieldErrorMessage = (fieldName, validationState) => {
  if (fieldName === "zabanAmount") {
    return validationState.zabanError || HAKLAI_PROPERTY_PART_ERRORS.zabanRequired;
  }
  return HAKLAI_PROPERTY_PART_ERRORS.required;
};

// Drives the row-level validation UI. `cellErrors[rowKey][fieldName] = message`
// is consumed by each row to render the red "!" indicator + ARIA error text
// per input.
//
// Gating rules (kept here so they stay symmetric across sources):
//   - A field's error surfaces if the row is committed OR the field has
//     been touched, OR the error is a "semantic" one that we want to
//     reveal immediately (a זב"ן amount that cannot be saved, and value
//     overshoot on `value`).
//   - A trailing-ignorable row with zero interaction is suppressed
//     entirely, regardless of which errors it technically has.
export const buildPropertyPartsValidationOverlay = ({
  groups,
  touchedFieldsByRow = {},
  committedRows = {},
  hasZaban = false,
  runningSaleBalanceByKey = {},
  validationStateByKey = null
} = {}) => {
  const renderableRows = groups?.allRows || [
    ...(groups?.residentialRows || []),
    ...(groups?.commercialRows || []),
    ...(groups?.landRows || []),
    ...(groups?.commercialBgRows || [])
  ];
  const cellErrors = {};

  // "Tipping row": the first renderable row whose cumulative allocation
  // pushes the running sum past `totalPropertyCost`. Every row after it
  // will also have a negative balance, but we flag only the row that
  // actually crossed the cap.
  let overshootRowKey = null;
  for (const [rowKey] of renderableRows) {
    if (Number(runningSaleBalanceByKey?.[rowKey]) < 0) {
      overshootRowKey = rowKey;
      break;
    }
  }

  renderableRows.forEach(([rowKey, part]) => {
    const categoryRows = getCategoryRows(groups, part?.category);
    const validationState = validationStateByKey?.[rowKey] || getPropertyPartRowValidationState({
      rowKey, part, categoryRows, hasZaban
    });
    const rowTouched = touchedFieldsByRow[rowKey] || {};
    const isCommitted = Boolean(committedRows[rowKey]);
    const hasRowInteraction = isCommitted || Object.keys(rowTouched).length > 0;

    if (validationState.isTrailingIgnorable && !hasRowInteraction) return;

    const hasOvershoot = rowKey === overshootRowKey;

    const rowCellErrors = {};
    const pushError = (fieldName, message) => {
      if (rowCellErrors[fieldName]) return;
      rowCellErrors[fieldName] = message;
    };

    for (const fieldName of validationState.invalidFields) {
      const isImmediateZabanError = fieldName === "zabanAmount" && validationState.immediateZabanError;
      if (isCommitted || Boolean(rowTouched[fieldName]) || isImmediateZabanError) {
        pushError(fieldName, getPropertyPartFieldErrorMessage(fieldName, validationState));
      }
    }

    // Over-allocation is a semantic cross-row error — it fires the moment
    // a user types a number that tips the running sum past the total sale
    // value, without waiting for blur/commit (same philosophy as
    // `immediateBuiltContradiction`). Deduped against any readiness error
    // on the same field via `pushError`.
    if (hasOvershoot) {
      pushError("value", PROPERTY_PART_VALUE_OVERSHOOT_MESSAGE);
    }

    if (Object.keys(rowCellErrors).length > 0) {
      cellErrors[rowKey] = rowCellErrors;
    }
  });

  return { cellErrors };
};

// Form-level + depreciation-gap gates live in `haklaiPropertyPartsGates.js` so
// `preCalc/logic/haklaiPreCalcValidation.js` does not import this entire module.

// ─── UI group helpers ────────────────────────────────────────────────────────

export const buildPropertyPartGroups = (propertyPartsData = {}) => {
  const residentialRows = buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.RESIDENTIAL);
  const commercialRows = buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.COMMERCIAL);
  const commercialBgRows = buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.COMMERCIAL_BG);
  const landRows = buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.LAND_AND_SHARED);

  const rowsByCategory = {
    [CATEGORIES.RESIDENTIAL]: residentialRows,
    [CATEGORIES.COMMERCIAL]: commercialRows,
    [CATEGORIES.COMMERCIAL_BG]: commercialBgRows,
    [CATEGORIES.LAND_AND_SHARED]: landRows
  };

  const effectiveRowsByCategory = Object.keys(rowsByCategory).reduce((acc, categoryId) => {
    const rows = rowsByCategory[categoryId] || [];
    acc[categoryId] = rows.filter((entry, index) => !isIgnorableTrailingPropertyPartRow(rows, index));
    return acc;
  }, {});

  const allRows = residentialRows;
  const renderableRows = effectiveRowsByCategory[CATEGORIES.RESIDENTIAL];

  return {
    residentialRows,
    commercialRows,
    commercialBgRows,
    landRows,
    rowsByCategory,
    effectiveRowsByCategory,
    allRows,
    renderableRows,
    lastResidentialKey: residentialRows.length ? residentialRows[residentialRows.length - 1][0] : null
  };
};

export const getCategoryRows = (groups, categoryId) => groups?.rowsByCategory?.[categoryId] || [];

export const getCategoryAddState = ({ categoryId, propertyPartsData = {} }) => {
  const nextKey = getNextCategoryKey({ categoryId, propertyPartsData });
  return {
    mode: nextKey ? HAKLAI_ADD_MODES.ADD : HAKLAI_ADD_MODES.UNAVAILABLE,
    nextKey
  };
};

// Parse a raw input value into what should be written onto the part.
//
// Returns a tagged object instead of a bare value so over-limit money inputs
// can signal "ignore this keystroke" without colliding with a legitimate
// string/number/boolean value:
//   - `{ value }`    — commit `value` to the part's `fieldName`.
//   - `{ skip: true }` — swallow the keystroke (input exceeded MAX_MONEY_DIGITS).
export const parsePropertyPartFieldValue = (fieldName, rawValue) => {
  if (fieldName === "ptorType") {
    return { value: rawValue || HAKLAI_PTOR_TYPES.NONE };
  }

  if (fieldName === "constructionsDoneBeforeApril1997") {
    return { value: rawValue === true || rawValue === "true" };
  }

  return parseHaklaiClassicMoneyInput(rawValue, { maxDigits: MAX_MONEY_DIGITS });
};

const getComputedAcquisitionByRows = (renderableRows = [], totalAcquisitionCost) => {
  const normalizedTotalAcquisitionCost = Number(totalAcquisitionCost);
  const computed = {};

  renderableRows.forEach(([key]) => { computed[key] = 0; });

  if (!hasPositiveNumericValue(normalizedTotalAcquisitionCost)) return computed;

  const totalSaleValue = renderableRows.reduce((sum, [, part]) => sum + (Number(part?.value) || 0), 0);
  if (!(totalSaleValue > 0)) return computed;

  let assignedTotal = 0;
  const lastKey = renderableRows.length ? renderableRows[renderableRows.length - 1][0] : "";

  renderableRows.forEach(([key, part]) => {
    const partSaleValue = Number(part?.value) || 0;
    if (key === lastKey) {
      computed[key] = Math.round(normalizedTotalAcquisitionCost - assignedTotal);
      return;
    }
    const partAcquisitionCost = Math.round((partSaleValue / totalSaleValue) * normalizedTotalAcquisitionCost);
    computed[key] = partAcquisitionCost;
    assignedTotal += partAcquisitionCost;
  });

  return computed;
};

export const buildPropertyPartsComputationState = ({
  groups,
  totalPropertyCost = 0,
  totalAcquisitionCost = 0
}) => {
  const renderableRows = groups?.renderableRows || [];
  const computedAcquisition = getComputedAcquisitionByRows(renderableRows, totalAcquisitionCost);
  const filledRows = renderableRows.filter(([, part]) => (Number(part?.value) || 0) > 0);
  const runningSaleBalanceByKey = {};

  if (Number(totalPropertyCost) > 0) {
    let allocatedSoFar = 0;
    filledRows.forEach(([rowKey, part]) => {
      allocatedSoFar += Number(part?.value) || 0;
      runningSaleBalanceByKey[rowKey] = Number(totalPropertyCost) - allocatedSoFar;
    });
  }

  return {
    computedAcquisition,
    runningSaleBalanceByKey
  };
};
